import { z } from 'zod'

import { PaymentProviderSchema } from './shop-orders'
import { SecretPatchSchema, SecretStatusSchema } from './tracking'

// Online store payments (CMS → Store → Payments, admins only): which gateway takes the money and its credentials.
// The credentials go in and never come back: the API keeps them sealed in the site's D1 and shows "set, …last 4".

export const PAYMENT_MODES = ['test', 'live'] as const
export const PaymentModeSchema = z.enum(PAYMENT_MODES)
export type PaymentMode = z.infer<typeof PaymentModeSchema>

/** Xendit secret keys say which mode they belong to; a key from the other mode is refused. */
export const XENDIT_KEY_PREFIX: Record<PaymentMode, string> = { test: 'xnd_development_', live: 'xnd_production_' }

export const PaymentSettingsViewSchema = z.object({
  provider: PaymentProviderSchema,
  mode: PaymentModeSchema,
  xendit: z.object({ secretKey: SecretStatusSchema, callbackToken: SecretStatusSchema }),
  /** The address to register in Xendit's dashboard (Settings → Webhooks → Payment Sessions). */
  callbackUrl: z.string(),
  /** Whether this server can seal credentials (PAYMENT_ENCRYPTION_KEY). Without it nothing can be saved. */
  encryptionReady: z.boolean(),
})
export type PaymentSettingsView = z.infer<typeof PaymentSettingsViewSchema>

export const PaymentSettingsUpdateSchema = z
  .object({
    provider: PaymentProviderSchema,
    mode: PaymentModeSchema,
    xendit: z.object({ secretKey: SecretPatchSchema, callbackToken: SecretPatchSchema }),
  })
  .superRefine((v, ctx) => {
    const key = v.xendit.secretKey
    if (key.action === 'set' && !key.value.startsWith(XENDIT_KEY_PREFIX[v.mode])) {
      ctx.addIssue({ code: 'custom', path: ['xendit', 'secretKey'], message: `key_mode:${XENDIT_KEY_PREFIX[v.mode]}` })
    }
    if (key.action === 'set' && !/^xnd_(development|production)_[A-Za-z0-9]{20,200}$/.test(key.value)) {
      ctx.addIssue({ code: 'custom', path: ['xendit', 'secretKey'], message: 'key_format' })
    }
    const token = v.xendit.callbackToken
    if (token.action === 'set' && !/^[A-Za-z0-9]{20,200}$/.test(token.value)) {
      ctx.addIssue({ code: 'custom', path: ['xendit', 'callbackToken'], message: 'token_format' })
    }
  })
export type PaymentSettingsUpdate = z.infer<typeof PaymentSettingsUpdateSchema>

/** What the checkout needs to know before "Place order": who takes the payment. */
export const CheckoutPaymentInfoSchema = z.object({ provider: PaymentProviderSchema })
export type CheckoutPaymentInfo = z.infer<typeof CheckoutPaymentInfoSchema>
